/**
 * How a dropped door's line reads on a screen (`./dropped-door.ts` says what it is).
 *
 * Three things change for it, and only for it — every other line draws exactly as before:
 *
 *   • the size cell says what the line is, in words: «Totale negozio · serie di taglie non
 *     componibile», where a size would be (never «Size not named» with a badge, which reads as a
 *     defect of the size master);
 *   • its request is not drawn as a request beside a proposal of nought. It is the need the run could
 *     not serve, said as that: «Fabbisogno non servibile (sotto la serie minima)»;
 *   • an aggregated row that holds such need says how much of its request it is.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { ProposalSummary } from '@/api/generated';
import { formatInteger } from '@/lib/format';
import { SizeCell, SizeLabel, sizeName, useSizeText, type SizeFields } from '@/planning';
import { NumericCell } from '@/primitives';

import { DROPPED_DOOR_KEY, isDroppedDoorLine, type DroppedDoorCandidate } from './dropped-door';
import { ABSENT_MEASURE } from './panels';

type SizedLine = SizeFields & Partial<DroppedDoorCandidate>;

/**
 * A line's size as words — an `aria-label`, a title, a joined reference. The dropped door names
 * itself; every other line is `useSizeText()` over its name, as before.
 */
export function useProposalSizeText(): (line: SizedLine, withScale: boolean) => string {
  const { t } = useTranslation();
  const sizeText = useSizeText();
  return useCallback(
    (line, withScale) => (isDroppedDoorLine(line) ? t(DROPPED_DOOR_KEY.size) : sizeText(sizeName(line), withScale)),
    [t, sizeText],
  );
}

/** A line's size inline: the size, or the dropped door in words. */
export function ProposalSizeLabel({ line, withScale = false }: {
  readonly line: SizedLine;
  readonly withScale?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  if (!isDroppedDoorLine(line)) return <SizeLabel name={sizeName(line)} withScale={withScale} />;
  return <span data-dropped-door="" className="text-ink-primary">{t(DROPPED_DOOR_KEY.size)}</span>;
}

/** A line's size as the `<td>` (or the row's `<th>`) a `DataColumn`'s `cell` returns. */
export function ProposalSizeCell({ line, withScale = false, className, as = 'td' }: {
  readonly line: SizedLine;
  readonly withScale?: boolean;
  readonly className?: string;
  readonly as?: 'td' | 'th';
}): React.JSX.Element {
  if (!isDroppedDoorLine(line)) {
    return <SizeCell name={sizeName(line)} withScale={withScale} {...(className === undefined ? {} : { className })} as={as} />;
  }
  const classes = [as === 'th' ? 'text-start font-normal' : null, className].filter(Boolean).join(' ');
  const label = <ProposalSizeLabel line={line} withScale={withScale} />;
  return as === 'th'
    ? <th scope="row" className={classes}>{label}</th>
    : <td {...(classes === '' ? {} : { className: classes })}>{label}</td>;
}

/**
 * The «requested» cell of a line table. An ordinary line: the number. A dropped door's: the number,
 * and under it what it is — the need the run could not serve, never a request the engine turned down.
 */
export function RequestedCell({ line }: {
  readonly line: Pick<ProposalSummary, 'requested_units'> & Partial<DroppedDoorCandidate>;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  if (!isDroppedDoorLine(line)) return <NumericCell value={line.requested_units} absent={ABSENT_MEASURE} />;
  return (
    <td className="py-1 text-end" data-dropped-need={line.requested_units}>
      <span className="font-numeric text-numeric text-ink-primary tabular-nums">{formatInteger(line.requested_units, i18n.language)}</span>
      <span className="text-ink-secondary block text-[11px] leading-tight">{t(DROPPED_DOOR_KEY.need)}</span>
    </td>
  );
}

/**
 * The «requested» cell of an aggregated row (a store's articles, an article's sizes): the number, and
 * — only when some of it is dropped-door need — how much, so the row's request is not read as pairs
 * the engine could have sent.
 */
export function RequestedWithDroppedCell({ requested, dropped }: {
  readonly requested: number;
  /** Pairs of `requested` that are dropped-door need; 0 or absent: nothing to say. */
  readonly dropped: number | undefined;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  if (dropped === undefined || dropped <= 0) return <NumericCell value={requested} absent={ABSENT_MEASURE} />;
  return (
    <td className="py-1 text-end" data-dropped-need={dropped}>
      <span className="font-numeric text-numeric text-ink-primary tabular-nums">{formatInteger(requested, i18n.language)}</span>
      <span className="text-ink-secondary block text-[11px] leading-tight">
        {t(DROPPED_DOOR_KEY.needPart, { pairs: formatInteger(dropped, i18n.language) })}
      </span>
    </td>
  );
}

/**
 * A count of a group's parts (its articles, its stores) and, only where some are dropped doors, how many
 * (bata-platform ADR 6183): «12» and under it «di cui 3 scartati». The count is the group's own; the part is
 * the platform's figure for it, never recounted from the rows a screen holds.
 */
export function CountWithDroppedCell({ count, dropped, alongside = false }: {
  readonly count: number;
  /** How many are dropped; 0 or absent: nothing to say. */
  readonly dropped: number | undefined;
  /** `dropped` are NOT part of `count` (a served count): «e altri 3 scartati», not «di cui 3 scartati». */
  readonly alongside?: boolean;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  if (dropped === undefined || dropped <= 0) return <NumericCell value={count} absent={ABSENT_MEASURE} />;
  return (
    <td className="py-1 text-end" data-dropped-count={dropped}>
      <span className="font-numeric text-numeric text-ink-primary tabular-nums">{formatInteger(count, i18n.language)}</span>
      <span className="text-ink-secondary block text-[11px] leading-tight">
        {t(alongside ? DROPPED_DOOR_KEY.countAlso : DROPPED_DOOR_KEY.countPart, { count: formatInteger(dropped, i18n.language) })}
      </span>
    </td>
  );
}
